import React, { useState } from 'react';
import { 
  MessageCircle, 
  Bug, 
  Lightbulb, 
  AlertTriangle, 
  Link as LinkIcon, 
  Type, 
  Languages, 
  Stethoscope,
  X,
  Send,
  Star,
  Check
} from 'lucide-react';
import { getThemeClass } from '../../styles/theme';

interface FeedbackSubmission {
  type: 'idea' | 'bug' | 'usability' | 'broken_link' | 'typo' | 'translation' | 'medical_review';
  title: string;
  description: string;
  page: string;
  priority: 'low' | 'medium' | 'high';
  rating?: number;
  email?: string;
  name?: string;
}

interface ContributorCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  examples: string[];
}

const contributorCategories: ContributorCategory[] = [
  {
    id: 'code',
    name: 'Code Contribution',
    description: 'Contributed code, bug fixes, or technical improvements',
    icon: <Bug className="h-4 w-4" />,
    color: 'bg-blue-100 text-blue-700',
    examples: ['Bug fixes', 'Feature development', 'Performance improvements', 'Security patches']
  },
  {
    id: 'design',
    name: 'Design & UX',
    description: 'Improved user interface, user experience, or visual design',
    icon: <Star className="h-4 w-4" />,
    color: 'bg-purple-100 text-purple-700',
    examples: ['UI improvements', 'UX research', 'Accessibility enhancements', 'Visual design']
  },
  {
    id: 'bug_identification',
    name: 'Bug Hunter',
    description: 'Identified and reported bugs or technical issues',
    icon: <AlertTriangle className="h-4 w-4" />,
    color: 'bg-red-100 text-red-700',
    examples: ['Bug reports', 'Issue reproduction', 'Edge case discovery', 'System testing']
  },
  {
    id: 'bug_fix',
    name: 'Bug Resolver',
    description: 'Provided solutions or fixes for reported bugs',
    icon: <Check className="h-4 w-4" />,
    color: 'bg-green-100 text-green-700',
    examples: ['Bug resolution', 'Patch development', 'Workaround solutions', 'Fix verification']
  },
  {
    id: 'ui_issue_report',
    name: 'UI Quality Assurance',
    description: 'Reported user interface issues or usability problems',
    icon: <MessageCircle className="h-4 w-4" />,
    color: 'bg-orange-100 text-orange-700',
    examples: ['Usability issues', 'Interface bugs', 'Responsive design problems', 'Navigation issues']
  },
  {
    id: 'broken_link_report',
    name: 'Link Validator',
    description: 'Found and reported broken links or navigation issues',
    icon: <LinkIcon className="h-4 w-4" />,
    color: 'bg-yellow-100 text-yellow-700',
    examples: ['Broken links', 'Dead pages', 'Navigation errors', 'Redirect issues']
  },
  {
    id: 'grammar_typo',
    name: 'Editor & Proofreader',
    description: 'Corrected grammar, spelling, or content errors',
    icon: <Type className="h-4 w-4" />,
    color: 'bg-indigo-100 text-indigo-700',
    examples: ['Grammar corrections', 'Spelling fixes', 'Content improvements', 'Style consistency']
  },
  {
    id: 'translation',
    name: 'Translator',
    description: 'Provided translations or language localization services',
    icon: <Languages className="h-4 w-4" />,
    color: 'bg-teal-100 text-teal-700',
    examples: ['Language translations', 'Localization', 'Cultural adaptations', 'Multi-language support']
  },
  {
    id: 'medical_review',
    name: 'Medical Reviewer',
    description: 'Provided medical expertise and content validation (verified credentials)',
    icon: <Stethoscope className="h-4 w-4" />,
    color: 'bg-emerald-100 text-emerald-700',
    examples: ['Medical content review', 'Clinical accuracy validation', 'Health information verification', 'Professional consultation']
  }
];

interface UserFeedbackWidgetProps {
  currentPage?: string;
  isFixed?: boolean;
}

export const UserFeedbackWidget: React.FC<UserFeedbackWidgetProps> = ({ 
  currentPage = window.location.pathname,
  isFixed = true 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'select' | 'form' | 'success'>('select');
  const [selectedType, setSelectedType] = useState<FeedbackSubmission['type'] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Partial<FeedbackSubmission>>({
    page: currentPage,
    priority: 'medium'
  });

  const feedbackTypes = [
    {
      id: 'idea' as const,
      name: 'Feature Idea',
      description: 'I wish this page/view did ___',
      icon: <Lightbulb className="h-5 w-5" />,
      color: 'bg-yellow-50 border-yellow-200 text-yellow-700',
      placeholder: 'Describe your feature idea or improvement suggestion...'
    },
    {
      id: 'bug' as const,
      name: 'Bug Report',
      description: 'I have a problem: ___',
      icon: <Bug className="h-5 w-5" />,
      color: 'bg-red-50 border-red-200 text-red-700',
      placeholder: 'Describe the bug or technical issue you encountered...'
    },
    {
      id: 'usability' as const,
      name: 'Usability Issue',
      description: 'This is confusing or hard to use',
      icon: <MessageCircle className="h-5 w-5" />,
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      placeholder: 'Describe what was confusing or difficult to use...'
    },
    {
      id: 'broken_link' as const,
      name: 'Broken Link',
      description: 'A link or feature is not working',
      icon: <LinkIcon className="h-5 w-5" />,
      color: 'bg-orange-50 border-orange-200 text-orange-700',
      placeholder: 'Describe which link or feature is broken...'
    },
    {
      id: 'typo' as const,
      name: 'Grammar/Typo',
      description: 'I found a spelling or grammar error',
      icon: <Type className="h-5 w-5" />,
      color: 'bg-purple-50 border-purple-200 text-purple-700',
      placeholder: 'Point out the specific error and suggested correction...'
    },
    {
      id: 'translation' as const,
      name: 'Translation Help',
      description: 'I can help with language translation',
      icon: <Languages className="h-5 w-5" />,
      color: 'bg-teal-50 border-teal-200 text-teal-700',
      placeholder: 'Which languages can you help translate? What content needs translation?'
    },
    {
      id: 'medical_review' as const,
      name: 'Medical Review',
      description: 'Medical professional offering content review',
      icon: <Stethoscope className="h-5 w-5" />,
      color: 'bg-green-50 border-green-200 text-green-700',
      placeholder: 'Describe your medical credentials and what content you can review...'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Here would be the actual API call to submit feedback
    console.log('Feedback submitted:', { ...formData, type: selectedType });

    setIsSubmitting(false);
    setStep('success');
    
    // Reset after success
    setTimeout(() => {
      setStep('select');
      setSelectedType(null);
      setFormData({ page: currentPage, priority: 'medium' });
      setIsOpen(false);
    }, 3000);
  };

  const selectedTypeConfig = feedbackTypes.find(t => t.id === selectedType);

  return (
    <>
      {/* Feedback Button */}
      <div className={`${isFixed ? 'fixed bottom-6 right-6 z-50' : 'relative'}`}>
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 group"
          title="Send Feedback"
        >
          <MessageCircle className="h-5 w-5 group-hover:rotate-12 transition-transform" />
        </button>
      </div>

      {/* Feedback Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className={`text-xl font-semibold ${getThemeClass('text', 'primary')}`}>Share Your Feedback</h3>
                <p className="text-sm text-gray-600 mt-1">Help us improve ZippyHealth.io</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              
              {/* Step 1: Select Feedback Type */}
              {step === 'select' && (
                <div className="space-y-4">
                  <h4 className={`font-medium ${getThemeClass('text', 'primary')} mb-4`}>What type of feedback do you have?</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {feedbackTypes.map((type) => (
                      <button
                        key={type.id}
                        onClick={() => {
                          setSelectedType(type.id);
                          setStep('form');
                        }}
                        className={`p-4 border-2 rounded-lg text-left transition-all hover:shadow-md ${type.color}`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-1">
                            {type.icon}
                          </div>
                          <div>
                            <h5 className="font-medium">{type.name}</h5>
                            <p className="text-sm opacity-80 mt-1">{type.description}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Feedback Form */}
              {step === 'form' && selectedTypeConfig && (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className={`p-4 border rounded-lg ${selectedTypeConfig.color}`}>
                    <div className="flex items-center space-x-3">
                      {selectedTypeConfig.icon}
                      <h4 className="font-medium">{selectedTypeConfig.name}</h4>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Brief Title
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Short summary of your feedback"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detailed Description
                    </label>
                    <textarea
                      required
                      value={formData.description || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      rows={4}
                      placeholder={selectedTypeConfig.placeholder}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority Level
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="low">Low - Nice to have</option>
                      <option value="medium">Medium - Moderately important</option>
                      <option value="high">High - Important or urgent</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.name || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="For contributor recognition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email (Optional)
                      </label>
                      <input
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="For follow-up if needed"
                      />
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                    <strong>Contributing to ZippyHealth.io:</strong> Your feedback helps improve the platform for everyone. 
                    Contributors may be recognized in our public changelog under categories like: code contribution, design improvement, 
                    bug identification, UI enhancement, content editing, translation services, or medical review.
                  </div>

                  <div className="flex justify-between">
                    <button
                      type="button"
                      onClick={() => setStep('select')}
                      className={`px-4 py-2 ${getThemeClass('text', 'tertiary')} hover:${getThemeClass('text', 'secondary')} transition-colors`}
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          <span>Submit Feedback</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {/* Step 3: Success */}
              {step === 'success' && (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <Check className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className={`text-xl font-semibold ${getThemeClass('text', 'primary')}`}>Thank You!</h4>
                  <p className="text-gray-600">
                    Your feedback has been submitted and will help us improve ZippyHealth.io. 
                    If we implement your suggestion or fix, you may be recognized as a contributor!
                  </p>
                  <div className="text-sm text-gray-500">
                    This window will close automatically in a few seconds...
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserFeedbackWidget; 
